import { ChevronDown, Loader2, Snowflake, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utils/cn'
import { PERMISSION_LEVELS, getPermissionLabel } from './permissionLevels'

interface PermissionSelectProps {
  canFreeze: boolean
  onChange: (canFreeze: boolean) => void
  /** When provided, adds a "Remove access" action below the levels */
  onRemove?: () => void
  disabled?: boolean
  isLoading?: boolean
  /** `field` renders a bordered trigger sized to sit next to an input */
  variant?: 'inline' | 'field'
  'aria-label'?: string
}

/**
 * Permission dropdown for a plan share (Google Docs style): pick the access
 * level and, for existing people, remove their access.
 */
export function PermissionSelect({
  canFreeze,
  onChange,
  onRemove,
  disabled,
  isLoading,
  variant = 'inline',
  'aria-label': ariaLabel,
}: PermissionSelectProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'field' ? 'outline' : 'ghost'}
          size="sm"
          disabled={disabled || isLoading}
          aria-label={ariaLabel}
          className={cn(
            'gap-1.5 px-2.5 font-normal cursor-pointer',
            variant === 'field'
              ? 'h-10 text-sm'
              : 'h-8 text-xs text-muted-foreground hover:text-foreground'
          )}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            canFreeze && <Snowflake className="h-3.5 w-3.5" />
          )}
          {getPermissionLabel(canFreeze)}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuRadioGroup
          value={canFreeze ? 'freeze' : 'edit'}
          onValueChange={value => {
            const next = value === 'freeze'
            if (next !== canFreeze) onChange(next)
          }}
        >
          {PERMISSION_LEVELS.map(level => (
            <DropdownMenuRadioItem
              key={level.value}
              value={level.value}
              className="items-start py-2 [&>span:first-child]:top-3"
            >
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{level.label}</p>
                <p className="text-xs text-muted-foreground">
                  {level.description}
                </p>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {onRemove && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onRemove}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <UserMinus className="h-4 w-4" />
              Remove access
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
